/**
 * A/B Test V7: Photo-Family Charm Price Ladder (3 arms)
 *
 * Even cookie-based split, site-wide (loaded on every page via theme.liquid).
 *   Arm A = control     — ladder products stay at the live catalog price.
 *   Arm B = moderate    — $18→$22, $24→$28, $28→$34, $32→$38 (~+22%).
 *   Arm C = aggressive  — $18→$25, $24→$32, $28→$38, $32→$44 (~+40%).
 *
 * Target products carry the `wf-price-ladder` tag (the personalized
 * photo/portrait/locket charm family). Two coordinated halves, same proven
 * architecture as the V6 $6/$8 test:
 *   1. Cohort assignment + cart attribute `ab_charm_price_test_v7`. The
 *      Cart Transform function in wear-felicity-app reads that attribute and
 *      reprices ladder line items to the arm price (the real charge). The
 *      live catalog price is never changed, so if the function ever fails
 *      everyone safely pays the control price.
 *   2. Display override — Cart Transform changes the charged price but NOT the
 *      price Liquid renders. For arms B/C we rewrite flagged prices to the
 *      pre-rendered arm value (data-ladder-b / data-ladder-c) and patch the
 *      product-JSON endpoints the charm builder + predictive search read.
 */

(function () {
  'use strict';

  var COOKIE_NAME = 'ab_charm_v7';
  var COOKIE_DAYS = 30;
  var LS_KEY      = 'ab_charm_v7';
  var CART_ATTR   = 'ab_charm_price_test_v7';
  var GA4_EVENT   = 'ab_test_assignment';
  var GA4_TEST    = 'charm_price_test_v7';
  var LADDER_TAG  = 'wf-price-ladder';
  var ARMS        = ['A', 'B', 'C'];

  // Catalog cents -> arm cents. Keyed on the EXACT control price so a catalog
  // change makes a product silently drop out of the test (never an overcharge).
  var LADDER_CENTS = {
    1800: { B: 2200, C: 2500 },
    2400: { B: 2800, C: 3200 },
    2800: { B: 3400, C: 3800 },
    3200: { B: 3800, C: 4400 }
  };

  /* ------------------------------------------------------------------ cookies */
  var getCookie = (typeof ORW !== 'undefined' && ORW.getCookie)
    ? ORW.getCookie
    : function (name) {
        var nameEQ = name + '=';
        var parts = document.cookie.split(';');
        for (var i = 0; i < parts.length; i++) {
          var trimmed = parts[i].trim();
          if (trimmed.indexOf(nameEQ) === 0) return trimmed.substring(nameEQ.length);
        }
        return null;
      };

  var setCookie = (typeof ORW !== 'undefined' && ORW.setCookie)
    ? ORW.setCookie
    : function (name, value, days) {
        var expires = '';
        if (days) {
          var date = new Date();
          date.setTime(date.getTime() + days * 86400000);
          expires = '; expires=' + date.toUTCString();
        }
        document.cookie = name + '=' + value + expires + '; path=/';
      };

  /* -------------------------------------------------------------- assignment */
  function getVariant() {
    var params = new URLSearchParams(window.location.search);
    var override = params.get('ab_charm_v7');
    if (ARMS.indexOf(override) !== -1) {
      // Persist the override so the forced cohort carries across pages
      // (consistent manual QA: PDP, builder, cart all match).
      setCookie(COOKIE_NAME, override, COOKIE_DAYS);
      try { localStorage.setItem(LS_KEY, override); } catch (e) {}
      return override;
    }

    var stored = getCookie(COOKIE_NAME);
    if (ARMS.indexOf(stored) !== -1) return stored;

    try {
      var lsStored = localStorage.getItem(LS_KEY);
      if (ARMS.indexOf(lsStored) !== -1) {
        setCookie(COOKIE_NAME, lsStored, COOKIE_DAYS);
        return lsStored;
      }
    } catch (e) {}

    var r = Math.random();
    var variant = r < 1 / 3 ? 'A' : (r < 2 / 3 ? 'B' : 'C');
    setCookie(COOKIE_NAME, variant, COOKIE_DAYS);
    try { localStorage.setItem(LS_KEY, variant); } catch (e) {}
    return variant;
  }

  var VARIANT = getVariant();
  window.__charmLadderCohort = VARIANT;

  /* ------------------------------------------------- product-JSON price patch (B/C)
     The charm builder (ab-test-charm-v4.js + the native 1r-theme builder) reads
     charm prices from /products/<h>.json and /collections/<c>/products.json and
     computes totals client-side; the predictive search modal reads
     /search/suggest.json. We can't rewrite those computed totals after the
     fact, so for arms B/C we intercept fetch and reprice ladder-tagged
     products in those responses — display then matches the server-side Cart
     Transform charge. Installed synchronously before any builder runs. */
  if (VARIANT !== 'A' && window.fetch) {
    var nativeFetch = window.fetch;
    var PRODUCT_JSON_RE = /\/products(\/[^/?#]+)?\.js(on)?(\?|#|$)/;

    // Cents (.js endpoints, numbers) and dollar strings (.json endpoints).
    var bumpPrice = function (val) {
      if (val == null) return val;
      if (typeof val === 'number') {
        var hit = LADDER_CENTS[val];
        return hit ? hit[VARIANT] : val;
      }
      var n = Math.round(parseFloat(val) * 100);
      var hitS = LADDER_CENTS[n];
      return hitS ? (hitS[VARIANT] / 100).toFixed(2) : val;
    };

    var isLadderProduct = function (p) {
      var t = p && p.tags;
      if (!t) return false;
      if (typeof t === 'string') {
        t = t.split(',').map(function (s) { return s.trim(); });
      }
      return t.indexOf(LADDER_TAG) !== -1;
    };

    var surchargeProduct = function (p) {
      if (!isLadderProduct(p)) return;
      ['price', 'price_min', 'price_max'].forEach(function (k) {
        if (p[k] != null) p[k] = bumpPrice(p[k]);
      });
      (p.variants || []).forEach(function (v) {
        if (v.price != null) v.price = bumpPrice(v.price);
      });
    };

    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var isProductJson = PRODUCT_JSON_RE.test(url);
      var isSuggest = url.indexOf('/search/suggest.json') !== -1;
      if (!isProductJson && !isSuggest) return nativeFetch.apply(this, arguments);
      return nativeFetch.apply(this, arguments).then(function (res) {
        if (!res.ok) return res;
        return res.text().then(function (txt) {
          try {
            var data = JSON.parse(txt);
            if (data.product) surchargeProduct(data.product);
            if (data.products) data.products.forEach(surchargeProduct);
            if (data.resources && data.resources.results && data.resources.results.products) {
              data.resources.results.products.forEach(surchargeProduct);
            }
            // /products/<handle>.js returns the product object bare (unwrapped)
            if (data.variants && data.tags) surchargeProduct(data);
            return new Response(JSON.stringify(data), {
              status: res.status,
              statusText: res.statusText,
              headers: { 'Content-Type': res.headers.get('content-type') || 'application/json' }
            });
          } catch (e) {
            return new Response(txt, {
              status: res.status,
              statusText: res.statusText,
              headers: { 'Content-Type': res.headers.get('content-type') || 'application/json' }
            });
          }
        });
      });
    };
  }

  /* ----------------------------------------------------------- cart + tracking */
  function stampCart() {
    var attrs = {};
    attrs[CART_ATTR] = VARIANT;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    });
  }

  function track() {
    stampCart().catch(function () {});

    // Re-stamp whenever the cart changes so the attribute survives.
    if (typeof jQuery !== 'undefined') {
      jQuery(document.body).on('afterAddItem.ajaxCart', function () {
        stampCart().catch(function () {});
      });
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: GA4_EVENT,
      ab_test_name: GA4_TEST,
      ab_test_variant: VARIANT
    });

    window._learnq = window._learnq || [];
    window._learnq.push(['identify', { $charm_price_test_v7: VARIANT }]);
  }

  /* --------------------------------------------------- display override (B/C) */
  // Rewrite each flagged ladder price to its pre-rendered arm value. The target
  // string is produced by Liquid with the same money filter as the visible
  // price, so formatting/locale always match. We compare before writing to
  // avoid reacting to our own DOM mutations (no observer feedback loop).
  var ARM_ATTR = 'data-ladder-' + VARIANT.toLowerCase();

  function applyPriceOverrides(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-ladder-price][' + ARM_ATTR + ']');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var target = el.getAttribute(ARM_ATTR);
      if (target && el.textContent.trim() !== target) {
        el.textContent = target;
      }
    }
  }

  function watchForLadderPrices() {
    applyPriceOverrides(document);

    if (typeof MutationObserver === 'undefined') return;
    var observer = new MutationObserver(function (mutations) {
      // Re-apply on any DOM change — covers quick-view modals, ajax collection
      // pagination, predictive search, and PDP variant-switch price re-renders.
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length || mutations[i].type === 'characterData' ||
            mutations[i].type === 'childList') {
          applyPriceOverrides(document);
          return;
        }
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  /* ---------------------------------------------------------------------- init */
  function init() {
    track();
    if (VARIANT !== 'A') watchForLadderPrices();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
